import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import SiteFooter from "@/components/SiteFooter";

const LAST_UPDATED = "27 February 2026";
const SUPPORT_EMAIL = "TenantTrailx@Gmail.com";

/**
 * Refunds & Cancellations policy page.
 * Copy is supplied verbatim by the owner. Do NOT rewrite, summarise, or
 * adjust phrasing. Only the [DATE] and [support email] placeholders are
 * substituted with live values.
 */
export default function Refunds() {
  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <Navbar />
      <main
        className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16"
        data-testid="refunds-page"
      >
        <div className="mb-10">
          <div className="label-mono text-[#1B6C62] mb-3">Legal</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#0D2B52] mb-3">
            Refunds &amp; Cancellations
          </h1>
          <p className="text-sm text-[#6B7280] font-mono">
            Last updated: {LAST_UPDATED}
          </p>
        </div>

        <article className="space-y-8 text-[15px] text-[#1A1A1A] leading-relaxed font-serif">
          <section>
            <p>
              TenantTrail is a subscription service that gives you secure document storage, AI help from Compass, voice-to-statement, letter drafting, email delivery and evidence tools. These are live services that cost real money to run the moment you start using them, so please read this before you subscribe. Prices are in Australian dollars (AUD) and payments are processed securely by Stripe.
            </p>
            <p className="mt-3">
              Nothing in this policy takes away your rights under the <strong>Australian Consumer Law</strong> - see &ldquo;Your Australian Consumer Law rights&rdquo; below.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Cancelling your plan</h2>
            <p>
              You can cancel any subscription at any time from your <strong>Account</strong> page. When you cancel:
            </p>
            <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
              <li>Your plan keeps working until the end of the period you&rsquo;ve already paid for.</li>
              <li>You won&rsquo;t be charged again after that.</li>
              <li>Your documents stay available to you until the end of that period, and you can download everything before it ends.</li>
            </ul>
            <p className="mt-3">
              You don&rsquo;t need to contact us to cancel - it&rsquo;s a one-click action - but we&rsquo;re happy to help if you get stuck.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Refunds - change of mind and used time</h2>
            <p>
              Because TenantTrail is a live service with real usage costs (AI, secure storage and email delivery begin the moment you start), we don&rsquo;t offer refunds for:
            </p>
            <ul className="list-disc list-outside pl-5 mt-3 space-y-2">
              <li><strong>Change of mind</strong> - deciding you no longer want the service.</li>
              <li><strong>Time already used</strong> - we don&rsquo;t refund unused days in a billing period you&rsquo;ve already paid for. Instead, cancelling stops your <em>next</em> charge while letting you use what you&rsquo;ve paid for until it runs out.</li>
            </ul>
            <p className="mt-3">
              This applies to monthly plans, annual plans, the one-off <strong>Dispute Mode</strong> pass (which unlocks a large AI allowance straight away), and the one-off <strong>3-Year Shield Pass</strong> (which grants 3 years of Shield-tier access at the moment of purchase). For the 3-Year Pass specifically: we don&rsquo;t refund for change of mind once the pass has been activated. After 3 years your access lapses to the standard monthly Shield plan unless you choose otherwise. None of this affects your statutory rights under the Australian Consumer Law.
            </p>
            <p className="mt-3">
              If you&rsquo;re not sure TenantTrail is right for you, please start on the <strong>free plan</strong> first - it lets you keep your lease and bond receipt and try Compass at no cost, with no card required.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Billing errors and problems</h2>
            <p>
              If you&rsquo;ve been charged in error - for example, a duplicate charge, a charge after you cancelled, or an amount that doesn&rsquo;t match your plan - contact us and we&rsquo;ll investigate and put it right, including a refund where a charge was genuinely made in error.
            </p>
            <p className="mt-3">
              If something goes wrong with the service itself, please tell us. We&rsquo;d much rather fix the problem quickly than leave you stuck.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl text-[#0D2B52] mb-3">Your Australian Consumer Law rights</h2>
            <p>
              Our consumer guarantees under the Australian Consumer Law cannot be excluded. Among other things, our services must be provided with due care and skill and be reasonably fit for the purpose we describe.
            </p>
            <p className="mt-3">
              If TenantTrail fails to meet a consumer guarantee, you may be entitled to a remedy - and for a <strong>major failure</strong>, you may be entitled to cancel and receive a refund, or to compensation for any drop in value. This is in addition to anything else in this policy. Nothing here limits those rights.
            </p>
          </section>

          <section>
            <p>
              How to contact us:{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="underline hover:text-[#1B6C62]"
                data-testid="refunds-support-email"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p className="mt-3">
              For cancellations you don&rsquo;t need us, but for billing questions, problems with the service, or anything covered above, email our support team at{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="underline hover:text-[#1B6C62]"
              >
                <strong>{SUPPORT_EMAIL}</strong>
              </a>
              . We answer within one business day, often sooner. If your matter is urgent - for example you&rsquo;re heading to a tribunal that week - say so in the subject line and we&rsquo;ll prioritise it.
            </p>
          </section>

          <hr className="border-[#E5E4DE]" />

          <p className="text-[13px] text-[#6B7280] italic">
            TenantTrail - Protection &middot; Evidence &middot; Accountability. Made in Australia, for Australian renters.
          </p>

          <p className="text-[13px] text-[#6B7280]">
            See also:{" "}
            <Link to="/terms" className="underline hover:text-[#0D2B52]">Terms of Service</Link>,{" "}
            <Link to="/privacy" className="underline hover:text-[#0D2B52]">Privacy Policy</Link>.
          </p>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}
